'use client'

import React, { useState, useEffect, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useSupabase } from '@/hooks/useSupabase'
import { getSiteUrl } from '@/lib/utils'

function AuthUIContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { supabase: supabaseClient, user, loading: authLoading } = useSupabase()
  const [checkingProfile, setCheckingProfile] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [agreePrivacy, setAgreePrivacy] = useState(false)
  const [agreeMarketing, setAgreeMarketing] = useState(false)
  const [marketingEmail, setMarketingEmail] = useState(false)
  const [marketingPush, setMarketingPush] = useState(false)
  const [marketingSms, setMarketingSms] = useState(false)

  useEffect(() => {
    if (authLoading) return

    if (!supabaseClient) {
      setCheckingProfile(false)
      return
    }

    const checkExistingProfile = async () => {
      try {
        // URL에서 redirect 파라미터 읽기
        const redirectParam = searchParams.get('redirect')
        if (redirectParam && typeof window !== 'undefined') {
          sessionStorage.setItem('authRedirect', redirectParam)
        }

        // user가 없으면 세션을 직접 확인하여 리다이렉트 루프 방지
        // 세션이 있으면 session.user로 바로 프로필 체크 진행
        let effectiveUser = user
        if (!effectiveUser) {
          // 로그인 직후 쿠키가 아직 설정되지 않았을 수 있으므로, 약간의 지연 후 확인
          await new Promise(resolve => setTimeout(resolve, 300))
          
          const { data: { user }, error: authError } = await supabaseClient.auth.getUser()
          
          // 사용자 확인 실패 시 에러 로그만 남기고 계속 진행 (쿠키가 아직 설정 중일 수 있음)
          if (authError) {
            console.warn('[AuthUI] 사용자 확인 실패 (계속 진행):', authError.message)
          }
          
          // 사용자가 있으면 바로 사용
          if (user) {
            effectiveUser = user
          } else {
            // 사용자도 없으면 로그인 페이지 표시
            setCheckingProfile(false)
            return
          }
        }

        // 로그인 상태에서는 auth 페이지에 머물지 않고 기본 경로로 이동
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''
        const safeRedirect =
          redirectParam && redirectParam !== currentPath && redirectParam !== '/auth'
            ? redirectParam
            : '/'
        
        // 기존 프로필 확인 (accounts는 프리랜서 전용)
        // is_active = true이고 is_onboarded = true인 프로필만 확인
        // effectiveUser.id 사용 (user가 없어도 session.user로 체크 가능)
        const { data: profile, error: profileError } = await supabaseClient
          .from('accounts')
          .select('profile_id, is_onboarded')
          .eq('user_id', effectiveUser.id)
          .eq('is_active', true)
          .is('deleted_at', null)
          .maybeSingle()
        
        // 에러 처리: 컬럼이 없거나 권한 문제인 경우
        if (profileError) {
          // 컬럼이 존재하지 않는 경우 (is_onboarded 컬럼이 없을 수 있음)
          if (profileError.code === '42703' || profileError.code === 'PGRST116' || profileError.code === '400') {
            // 프로필이 있으면 통과 (is_onboarded 컬럼이 없으므로 온보딩 완료로 간주)
            if (profile) {
              router.push(safeRedirect)
              return
            }
            return
          }
          console.warn('프로필 확인 실패:', profileError)
          return
        }
        
        // 활성 프로필이 있고 온보딩이 완료된 경우만 true
        const hasValidProfile = !!profile && profile.is_onboarded !== false
        if (hasValidProfile) {
          router.push(safeRedirect)
          return
        }
        
        // 프로필이 없거나 온보딩이 완료되지 않은 경우 온보딩으로 리다이렉트
        if (!hasValidProfile) {
          // 프로필이 없거나 is_onboarded가 false인 경우
          if (!profile || profile.is_onboarded === false) {
            console.log('프로필이 없거나 온보딩이 완료되지 않음, 온보딩으로 리다이렉트')
            router.push('/onboarding')
            return
          }
        }
      } catch (error) {
        console.error('프로필 확인 실패:', error)
      } finally {
        setCheckingProfile(false)
      }
    }

    checkExistingProfile()
  }, [authLoading, user, supabaseClient, searchParams, router])


  const allRequiredAgreed = agreeTerms && agreePrivacy
  const allAgreed = allRequiredAgreed && agreeMarketing && marketingEmail && marketingPush && marketingSms

  const handleToggleAllAgree = (checked: boolean) => {
    setAgreeTerms(checked)
    setAgreePrivacy(checked)
    setAgreeMarketing(checked)
    setMarketingEmail(checked)
    setMarketingPush(checked)
    setMarketingSms(checked)
  }

  const signInWithProvider = async (provider: 'google' | 'kakao') => {
    if (isSubmitting) return

    try {
      if (!supabaseClient) {
        setLoginError('로그인 서비스를 초기화하지 못했습니다. 잠시 후 다시 시도해주세요.')
        return
      }

      if (!allRequiredAgreed) {
        setLoginError('필수 약관에 동의해야 가입/로그인을 진행할 수 있어요.')
        return
      }

      setLoginError(null)
      setIsSubmitting(true)

      // PKCE 동작을 위해 콜백 URL은 반드시 "지금 보는 페이지와 같은 origin"이어야 함.
      // 다른 도메인(예: .env의 NEXT_PUBLIC_AUTH_REDIRECT_TO가 배포 URL)으로 보내면
      // code_verifier가 해당 origin 쿠키에 없어 "PKCE code verifier not found" 발생.
      const origin = typeof window !== 'undefined' ? window.location.origin : getSiteUrl()
      
      console.log('🔍 OAuth 로그인 시작:', { provider, origin })

      // sessionStorage에서 redirect 가져오기 (없으면 홈으로)
      // NEXT_PUBLIC_AUTH_REDIRECT_HOME 환경 변수가 있으면 사용
      const authRedirectHome = typeof process !== 'undefined'
        ? process.env.NEXT_PUBLIC_AUTH_REDIRECT_HOME
        : null
      
      // redirectUrl을 경로만 추출 (절대 URL이면 경로만 사용)
      let redirectUrl = (typeof window !== 'undefined' && sessionStorage.getItem('authRedirect')) || authRedirectHome || '/'
      
      // 절대 URL인 경우 경로만 추출
      if (typeof window !== 'undefined') {
        try {
          const url = new URL(redirectUrl)
          // 같은 origin이면 pathname만 사용
          if (url.origin === window.location.origin) {
            redirectUrl = url.pathname + url.search
          } else {
            // 다른 origin이면 보안상 '/'로 설정
            redirectUrl = '/'
          }
        } catch {
          // URL 파싱 실패 시 (이미 상대 경로인 경우)
          // 그대로 사용하되, '/'로 시작하는지 확인
          if (!redirectUrl.startsWith('/')) {
            redirectUrl = '/'
          }
        }
      }
      
      // 항상 현재 origin 기준 콜백 URL 사용 (PKCE code_verifier가 같은 origin 쿠키에 있어야 함)
      const callbackUrl = `${origin}/auth/callback?next=${encodeURIComponent(redirectUrl)}`

      console.log('🔗 콜백 URL:', callbackUrl)
      console.log('🎯 최종 리다이렉트 URL:', redirectUrl)

      const { data, error } = await supabaseClient.auth.signInWithOAuth({
        provider,
        options: {
          // redirectTo는 반드시 콜백 URL이어야 함 (최종 목적지가 아님)
          redirectTo: callbackUrl,
          // Google OAuth: 항상 계정 선택 화면 표시
          queryParams: provider === 'google' 
            ? { prompt: 'select_account' }
            : undefined,
        },
      })

      if (error) {
        console.error('❌ OAuth 로그인 에러:', error)
        setLoginError(`로그인 중 오류가 발생했습니다: ${error.message}`)
        return
      }

      console.log('✅ OAuth 로그인 성공:', data)
    } catch (error: unknown) {
      console.error('❌ OAuth 로그인 예외 발생:', error)
      const message = error instanceof Error ? error.message : '알 수 없는 오류'
      setLoginError(`로그인 중 오류가 발생했습니다: ${message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGoogleLogin = async () => {
    await signInWithProvider('google')
  }

  const handleKakaoLogin = async () => {
    await signInWithProvider('kakao')
  }


  const isDisabled = checkingProfile || isSubmitting

  return (
    <section className="min-h-[100svh] bg-gradient-to-b from-blue-50 to-white">
      <main className="mx-auto flex min-h-[100svh] max-w-4xl items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="w-full max-w-[460px]">
          <div className="rounded-[22px] border border-gray-200 bg-white px-8 pb-8 pt-10 shadow-[0_12px_32px_rgba(15,23,42,0.08)]">
            <div className="mb-7 text-center">
              <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                <img src="/icon.svg" alt="LINKUS" className="h-10 w-10 rounded-lg" />
              </div>
              <p className="text-[40px] font-black leading-none tracking-tight text-gray-900">LINKUS</p>
              <h1 className="mt-3 text-[42px] font-extrabold leading-none tracking-tight text-gray-900">
                환영합니다!
              </h1>
              <p className="mt-4 text-xl font-medium text-gray-700">프로젝트 참여를 위해 로그인하세요.</p>
            </div>

            {checkingProfile && (
              <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4 text-center">
                <div className="mx-auto mb-2 h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                <p className="text-sm text-gray-600">확인 중…</p>
              </div>
            )}

            {loginError && (
              <div
                role="alert"
                className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {loginError}
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={handleGoogleLogin}
                disabled={isDisabled}
                aria-busy={isSubmitting}
                className={[
                  'flex h-14 w-full items-center justify-center gap-3 rounded-[14px] border text-2xl font-bold transition',
                  'focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2',
                  isDisabled
                    ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                    : 'border-gray-200 bg-white text-gray-900 hover:bg-gray-50 active:scale-[0.99]',
                ].join(' ')}
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>{isSubmitting ? 'Google 연결 중...' : 'Google로 계속'}</span>
              </button>

              <button
                onClick={handleKakaoLogin}
                disabled={isDisabled}
                aria-busy={isSubmitting}
                className={[
                  'flex h-14 w-full items-center justify-center gap-3 rounded-[14px] border text-2xl font-bold transition',
                  'focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2',
                  isDisabled
                    ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                    : 'border-[#FEE500] bg-[#FEE500] text-[#3C1E1E] hover:brightness-95 active:scale-[0.99]',
                ].join(' ')}
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="#3C1E1E"
                    d="M12 3C6.48 3 2 6.48 2 10.8c0 2.7 1.8 5.1 4.5 6.4L5.5 21l4.1-2.1c1.1.2 2.2.3 3.4.3 5.52 0 10-3.48 10-7.8S17.52 3 12 3z"
                  />
                </svg>
                <span>{isSubmitting ? 'Kakao 연결 중...' : 'Kakao로 계속'}</span>
              </button>
            </div>

            <section className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <h2 className="text-sm font-semibold text-gray-900">약관 동의</h2>

              <div className="mt-3 space-y-3 text-sm">
                <label className="flex items-center gap-2 font-medium text-gray-900">
                  <input
                    type="checkbox"
                    checked={allAgreed}
                    onChange={(e) => handleToggleAllAgree(e.target.checked)}
                    className="h-4 w-4"
                  />
                  전체 동의하기
                </label>
                <p className="text-xs text-gray-500">필수 항목에 동의해야 가입할 수 있어요. 선택 항목은 나중에 변경할 수 있어요.</p>

                <div className="space-y-2 rounded-lg bg-white p-3">
                  <label className="flex items-center justify-between gap-2 text-gray-900">
                    <span>
                      <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} className="mr-2 h-4 w-4" />
                      (필수) 이용약관 동의
                    </span>
                    <a href="/terms" className="text-xs font-semibold text-blue-600 hover:underline">보기</a>
                  </label>
                  <p className="pl-6 text-xs text-gray-500">서비스 이용 규칙, 책임 범위, 계정 정책을 포함해요.</p>

                  <label className="flex items-center justify-between gap-2 text-gray-900">
                    <span>
                      <input type="checkbox" checked={agreePrivacy} onChange={(e) => setAgreePrivacy(e.target.checked)} className="mr-2 h-4 w-4" />
                      (필수) 개인정보처리방침 동의
                    </span>
                    <a href="/privacy" className="text-xs font-semibold text-blue-600 hover:underline">보기</a>
                  </label>
                  <p className="pl-6 text-xs text-gray-500">수집 항목, 이용 목적, 보관 기간, 위탁, 제3자 제공 내용을 포함해요.</p>
                </div>

                <div className="space-y-2 rounded-lg bg-white p-3">
                  <label className="flex items-center gap-2 text-gray-900">
                    <input
                      type="checkbox"
                      checked={agreeMarketing}
                      onChange={(e) => {
                        const checked = e.target.checked
                        setAgreeMarketing(checked)
                        if (!checked) {
                          setMarketingEmail(false)
                          setMarketingPush(false)
                          setMarketingSms(false)
                        }
                      }}
                      className="h-4 w-4"
                    />
                    (선택) 마케팅 정보 수신 동의
                  </label>
                  <p className="pl-6 text-xs text-gray-500">이벤트, 신규 기능, 혜택 소식을 받아요. 동의하지 않아도 서비스 이용에 영향 없어요.</p>
                  <div className="pl-6 flex flex-wrap gap-4 text-xs text-gray-700">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={marketingEmail} disabled={!agreeMarketing} onChange={(e)=>setMarketingEmail(e.target.checked)} className="h-3.5 w-3.5" />이메일로 받기</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={marketingPush} disabled={!agreeMarketing} onChange={(e)=>setMarketingPush(e.target.checked)} className="h-3.5 w-3.5" />푸시로 받기</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={marketingSms} disabled={!agreeMarketing} onChange={(e)=>setMarketingSms(e.target.checked)} className="h-3.5 w-3.5" />SMS로 받기</label>
                  </div>
                </div>

                <p className="text-xs text-gray-500">거래 알림, 견적 알림 같은 서비스 운영 알림은 마케팅 동의와 별개로 제공돼요.</p>
                <p className="text-xs text-gray-500">가입을 진행하면 이용약관, 개인정보처리방침에 동의한 것으로 처리돼요.</p>
              </div>
            </section>
          </div>
        </div>
      </main>
    </section>
  )


}

const AuthUI = () => {
  return (
    // ✅ Suspense fallback도 같은 톤으로 통일 (AuthUI 컴포넌트 내부 fallback 교체)
<Suspense
  fallback={
    <section className="min-h-[100svh] bg-gradient-to-b from-blue-50 to-white">
      <main className="mx-auto flex min-h-[100svh] max-w-3xl items-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-sm">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="text-center py-8">
              <div className="mx-auto mb-3 h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
              <p className="text-sm text-gray-600 font-medium">로딩 중…</p>
            </div>
          </div>
        </div>
      </main>
    </section>
  }
>
  <AuthUIContent />
</Suspense>

  )
}

export default AuthUI
